/**
 * The register of builds.
 *
 * A build is a numbered, dated, signed unit, and the two things that tell you at a glance whether
 * the rebrand worked are the launcher icon and the screenshots. Both are already on disk by the
 * time a row exists, so the register shows them: the icon at the left of each line, the three
 * captures as contact prints at the right. Reading down the column is how you catch a client that
 * shipped with the wrong logo — which a table of slugs and SHAs cannot tell you.
 */
import type { Job, JobState } from '../api';

const VERDICT_WORD: Record<JobState, string> = {
  queued: 'Waiting',
  running: 'Building',
  passed: 'Passed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const SHOT_ORDER = ['screenshot:menu', 'screenshot:about', 'screenshot:player'];

function shots(job: Job) {
  return SHOT_ORDER
    .map((kind) => job.artifacts.find((a) => a.kind === kind && !a.prunedAt))
    .filter((shot): shot is NonNullable<typeof shot> => Boolean(shot));
}

export function Register({ jobs, showClient, names, ago, onOpen }: {
  jobs: Job[];
  showClient: boolean;
  names: Map<string, string>;
  ago: (at: number) => string;
  onOpen: (id: number) => void;
}) {
  return (
    <div className="register">
      {jobs.map((job) => {
        const icon = job.artifacts.find((a) => a.kind === 'playstore-icon' && !a.prunedAt);
        const prints = shots(job);
        return (
          <button type="button" className="entry" key={job.id} onClick={() => onOpen(job.id)}>
            <span className="entry-icon">
              {icon
                ? <img src={icon.url} alt="" loading="lazy" />
                : <span className="entry-icon-blank" aria-hidden="true" />}
            </span>

            <span className="entry-id">#{job.id}</span>

            <span className="entry-what">
              <span className="entry-name">
                {showClient ? (names.get(job.slug) ?? job.slug) : (job.versionName ?? 'version not read')}
              </span>
              <span className="entry-sub">
                {job.platform === 'ios' ? 'iOS ' : ''}
                {showClient && job.versionName ? `${job.versionName} ` : ''}
                ({job.versionCode ?? '—'}) from {job.commit ? job.commit.slice(0, 9) : job.ref}
              </span>
            </span>

            <span className={`verdict ${job.state}`}>
              {VERDICT_WORD[job.state]}
              {job.failure && <em>{job.failure.step}</em>}
            </span>

            <span className="entry-prints" aria-hidden="true">
              {prints.map((print) => <img key={print.kind} src={print.url} alt="" loading="lazy" />)}
            </span>

            <span className="entry-when">{ago(job.queuedAt)}</span>
          </button>
        );
      })}
    </div>
  );
}
